import React from 'react';
import './grid.css';
import { Label } from '@nooz-optics/nooz-components-lib';
import { Image } from '../Media/Image';
import { Component } from '../../Contentful/component';
import { ShopProductItem } from '../../Ecommerce/Shop/ProductItem/shopProductItem';
import { Body } from '../Elements/Body/body';
import { ShopProductPackItem } from '../../Ecommerce/Shop';
import { ScrollableTabs } from '../Navigation/ScrollableTabs';
import { useMediaQuery } from '../../../utils/hooks/useMediaQuery';
import { Button } from '../Elements';

interface IGridProps extends IGrid {
  hadHorizontalDivider: boolean;
  wrap: boolean;
}

export const PureGrid: React.FC<IGridProps> = ({
  space,
  isWhite,
  header,
  isSlider,
  gridItems,
  itemsWidth,
  buttons,
  gridCols,
  itemsGrow = true,
  targetDiopter,
}) => {
  const gridShopItemsNumber = gridItems?.filter((item) => !!item.product)
    .length;
  const isWindowSmallSize = useMediaQuery('lg');

  const gridButtons = buttons?.map((button) => <Button {...button} />);

  const returnGridHeader = () => {
    if (!header) {
      return <></>;
    }
    if (header.text) {
      return (
        <div className='block lg:px-10  w-full'>
          <div className='pt-6 w-full border-t xl:justify-between xl:flex'>
            <Label {...header} color={{ classname: 'gray-800' }} size='6xl' />
          </div>
        </div>
      );
    }
    if (header.richBody) {
      return <Body {...header} />;
    }
    if (header.path) {
      return <Component {...header} />;
    }

    return header;
  };

  const returnHeader = header && (
    <div className='flex xl:flex justify-center w-full'>
      <div className='pb-7 w-full'>{returnGridHeader()}</div>
    </div>
  );

  const returnGridItemContent = (item, index) => {
    if (item.product) {
      const isgridShopItemsNumberImpair = gridShopItemsNumber % 2 === 1;
      const isLastGridItem = index === gridItems.length - 1;
      const isFullWidth = isgridShopItemsNumberImpair && isLastGridItem;

      if (gridItems) {
        // return pack shop product item
        if (item.product.option1) {
          return <ShopProductPackItem {...item} />;
        } else {
          return <ShopProductItem {...item} targetDiopter={targetDiopter} />;
        }
      }
    } else if (item.media) {
      return <Image {...item} objectFit='cover' width='100%' />;
    } else if (item.path) {
      return <Component {...item} />;
    } else {
      return item;
    }
  };

  const returnGridSlider = () => {
    const gridSliderContent = () =>
      gridItems?.map((item, index) => {
        const itemWidth = item.width || itemsWidth || '';
        return (
          <div key={item?.id} className={`${itemWidth} px-2`}>
            {returnGridItemContent(item)}
          </div>
        );
      });

    return (
      <div
        className={`${isSlider} relative flex overflow-x-scroll overflow-y-visible snap-x no-scrollbar`}
      >
        {/* Display arrows only in desktop */}
        {!isWindowSmallSize && buttons ? (
          <ScrollableTabs
            shouldOverflow
            isWhite={isWhite}
            buttons={gridButtons}
          >
            {gridSliderContent()}
          </ScrollableTabs>
        ) : (
          <>{gridSliderContent()}</>
        )}
      </div>
    );
  };

  const returnGridWrap = () => {
    return (
      <div
        className={`grid grid-flow-row-dense grid-wrap gap-y-8 gap-x-3 lg:gap-x-5 ${gridCols}`}
      >
        {gridItems?.map((item, index) => returnGridItemContent(item, index))}
      </div>
    );
  };

  const returnGridItems = () => {
    switch (isSlider) {
      case 'hidden':
        return returnGridWrap();
      case 'block lg:hidden':
        return (
          <>
            {returnGridSlider()}
            <div className='w-full hidden lg:block'>{returnGridWrap()}</div>
          </>
        );
      case 'block':
        return returnGridSlider();
      default: {
        return returnGridWrap();
      }
    }
  };

  return (
    <div className='w-full h-full'>
      {/* insert header on top of grid */}
      {returnHeader}
      {returnGridItems()}
    </div>
  );
};

export const Grid = ({ grid }) => <PureGrid {...grid} />;
